//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { IIMRIFrameWrapper, IMRIFrameWrapperOptions, IMRIFrameWrapperMessage } from '../../../common/iframe-wrapper'
import { ToastHelper, LocalizationHelper, LogHelper, TypeHelper } from '../../../../helpers'
import { Enums } from '../../../../models/common'
import { OpDashboard } from '../../../../models/custom'

import { IIMRDXDashboardFilterComponent, IMRDXDashboardFilterOptions } from '../../components'
import { DXDashboardService, DXDashboardFilter } from '../../services'


// Public Types

export interface IIMRDXDashboardMultiComponent {
    toggleEditMode(): void
}

export type IMRDXDashboardMultiOptions = {
    dashboard: OpDashboard

    onInitialized?: (dashboard: IIMRDXDashboardMultiComponent) => void
}

// Public Types End


export class IMRDXDashboardMultiController implements ng.IComponentController, IIMRDXDashboardMultiComponent {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.Dashboard.DXDashboardMulti]' }

    // Consts End


    // Members

    private isPinned: boolean
    private isLoading: boolean
    private dashboardId: number
    private options: IMRDXDashboardMultiOptions

    private sourceIFrameWrapper: IIMRIFrameWrapper<any>
    private targetIFrameWrapper: IIMRIFrameWrapper<any>
    private sourceIFrameWrapperOptions: IMRIFrameWrapperOptions<any>
    private targetIFrameWrapperOptions: IMRIFrameWrapperOptions<any>

    private setParametersButtonOptions: DevExpress.ui.dxButtonOptions

    private filterComponent: IIMRDXDashboardFilterComponent
    private dxdashboardFilterOptions: IMRDXDashboardFilterOptions

    private pinDrawerButton: DevExpress.ui.dxButtonOptions

    // Members End


    // Ctor

    constructor(private dxdashboardService: DXDashboardService) { }

    // Ctor End


    // IComponentController Implementation

    public $onInit(): void {
        this.isLoading = true

        if (this.options != null && this.options.dashboard != null) {
            this.setOptions()

            $
                .when(
                    this.initializeTargetPart(this.options.dashboard),
                    this.initializeSourcePart(this.options.dashboard),
                    this.initializeFilterComponent(),
                    this.initializeParametersButton()
                )
                .then(() => {
                    this.isLoading = false

                    this.onInitialized()
                })
                .fail(() => {
                    ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(IMRDXDashboardMultiController.LOG_PREFIX, `Unable to initialize dxdashboard module.`)
                })
        }
    }

    // IComponentController Implementation End


    // IIMRDXDashboardMultiComponent Implementation

    public toggleEditMode(): void {
    }

    // IIMRDXDashboardMultiComponent Implementation End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    // Events End


    // Data Transfer

    private onSourceDashboardMessage(message: IMRIFrameWrapperMessage<any>): void {
        if (message != null) {
            switch (message.data.operation) {
                case 'GET_STATE_REPLY':
                    this.dxdashboardService.saveFilter(
                        this.dashboardId,
                        {
                            name: this.filterName,
                            state: message.data.data
                        }
                    );

                    this.filterComponent.refresh()
                    this.filterComponent.currentFilterName = this.filterName

                    break

                case 'GET_SELECTED_DATA_REPLY':
                    this.targetIFrameWrapper.sendMessage({
                        operation: 'UPDATE_DATA',
                        data: message.data.data
                    })

                    break
            }
        }
    }

    private requestSelectedDataFromSource(): void {
        this.dxdashboardService.setCurrentFilterName(this.dashboardId, this.filterComponent.currentFilterName)

        this.sourceIFrameWrapper.sendMessage({
            operation: 'GET_SELECTED_DATA'
        })
    }

    private sendDashboardStateToSource(state: any): void {
        if (state != null) {
            this.sourceIFrameWrapper.sendMessage({
                operation: 'SET_STATE',
                data: state
            })
        }
    }

    // Data Transfer End


    // Target/Source Side

    private initializeTargetPart(dashboard: OpDashboard): JQueryPromise<any> {
        return this.initializeTargetIFrame(dashboard)
    }

    private initializeSourcePart(dashboard: OpDashboard): JQueryPromise<any> {
        return this.initializeSourceIFrame(dashboard)
    }

    // Target/Source Side End


    // IFrame

    private initializeTargetIFrame(dashboard: OpDashboard): JQueryPromise<any> {
        const dashboardIndex = this.dxdashboardService.getDashboardIndex(dashboard)
        const targetSrc: string = dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_PATH, dashboardIndex)
        const sessionToken = dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_TOKEN, dashboardIndex)

        if (TypeHelper.isNullOrEmpty(targetSrc) || TypeHelper.isNullOrEmpty(sessionToken)) {
            LogHelper.warn(IMRDXDashboardMultiController.LOG_PREFIX, `No paths or tokens data for target dashboardId: ${dashboard.idDashboard}.`)

            return $.Deferred<any>().reject().promise()
        }

        this.targetIFrameWrapperOptions = {
            url: targetSrc + '&sessionToken=' + sessionToken,
            headers: {
                sourceApp: 'imr_simax_dxdashboard'
            },

            onInitialized: (targetIFrameWrapper: IIMRIFrameWrapper<any>) => this.targetIFrameWrapper = targetIFrameWrapper,
            onError: () => this.onIFrameError(),
        }

        return TypeHelper.onInitializedAll(this.targetIFrameWrapperOptions)
    }

    private initializeSourceIFrame(dashboard: OpDashboard): JQueryPromise<any> {
        const dashboardIndex = this.dxdashboardService.getDashboardIndex(dashboard)
        const sourceSrc: string = dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_MULTI_SOURCE_PATH, dashboardIndex)
        const sessionToken = dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_TOKEN, dashboardIndex)

        if (TypeHelper.isNullOrEmpty(sourceSrc) || TypeHelper.isNullOrEmpty(sessionToken)) {
            LogHelper.warn(IMRDXDashboardMultiController.LOG_PREFIX, `No paths or tokens data for source dashboardId: ${dashboard.idDashboard}.`)

            return $.Deferred<any>().reject().promise()
        }

        this.sourceIFrameWrapperOptions = {
            url: sourceSrc + '&sessionToken=' + sessionToken,
            headers: {
                sourceApp: 'imr_simax_dxdashboard',
            },

            // Second init for when the source iframe is hidden
            onInitialized: (sourceIFrameWrapper: IIMRIFrameWrapper<any>) => this.sourceIFrameWrapper = sourceIFrameWrapper,
            onMessage: (message: IMRIFrameWrapperMessage<any>) => this.onSourceDashboardMessage(message),
            onError: () => this.onIFrameError(),
        }

        if (this.dxdashboardService.getIsOpen(this.dashboardId)) {
            return TypeHelper.onInitializedAll(this.sourceIFrameWrapperOptions)
        }

        return $.Deferred<any>().resolve().promise()
    }

    private onIFrameError(): void {
        ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
        LogHelper.warn(IMRDXDashboardMultiController.LOG_PREFIX, `There was a problem with the iframe.`)
    }

    // IFrame End


    // Filters

    private filterName;
    private getSourceDashboardState(name: string): void {
        this.filterName = name
        this.sourceIFrameWrapper.sendMessage({
            operation: 'GET_STATE'
        })
    }

    private initializeFilterComponent(): JQueryPromise<any> {
        this.dxdashboardFilterOptions = {
            dashboardId: this.dashboardId,

            onInitialized: (filterComponent: IIMRDXDashboardFilterComponent) => this.filterComponent = filterComponent,
            onFilterAdded: (name: string) => this.getSourceDashboardState(name),
            onFilterChanged: (value: DXDashboardFilter) => {
                if (value != null) {
                    this.sendDashboardStateToSource(value.state)
                }
            }
        }

        if (this.dxdashboardService.getIsOpen(this.dashboardId)) {
            return TypeHelper.onInitializedAll(this.dxdashboardFilterOptions)
        }

        return $.Deferred<any>().resolve().promise()
    }

    // Filters End


    private initializeParametersButton(): JQueryPromise<any> {
        this.setParametersButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Set),

            onClick: () => {
                this.requestSelectedDataFromSource()
            }
        }

        if (this.dxdashboardService.getIsOpen(this.dashboardId)) {
            return TypeHelper.onInitializedAll(this.setParametersButtonOptions)
        }

        return $.Deferred<any>().resolve().promise()
    }

    // Parameters Button End

    // Options

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.dashboard != null) {
                this.dashboardId = this.options.dashboard.idDashboard
                this.isPinned = this.dxdashboardService.getIsPinned(this.dashboardId)
            }
        }
    }

    // Options End
}
